import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTranslation } from "react-i18next";
import {
  lightColorChanged,
  embeddedDataChanged,
  darkColorChanged,
  generateQrCodeAsync,
  QrCodeExportFormat,
  qrCodeWidthChanged,
  selectState,
  targetFormatChanged,
} from "./QrCodeGeneratorSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import { HomeCard } from "../../components/home_card/HomeCard";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import CompressionImage from "../../assets/Compression.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function QrCodeGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("qrCodeGenerator.title")}
      pageSeoTitle={t("qrCodeGenerator.seoTitle")}
      pageDescription={t("qrCodeGenerator.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("qrCodeGenerator.mainHeading")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <TextField
              fullWidth
              label={t("qrCodeGenerator.urlLabel")}
              variant="outlined"
              color="info"
              value={state.embeddedData}
              onChange={(e) => {
                dispatch(embeddedDataChanged(e.target.value));
              }}
            />
          </Grid2>
          <Grid2 xs={6}>
            <CustomSelect
              label={t("qrCodeGenerator.exportAs")}
              value={state.targetFormat}
              onChange={(e) => {
                dispatch(
                  targetFormatChanged(
                    Number(e.target.value) as QrCodeExportFormat
                  )
                );
              }}
            >
              <MenuItem value={QrCodeExportFormat.png}>PNG</MenuItem>
              <MenuItem value={QrCodeExportFormat.jpg}>JPG</MenuItem>
              <MenuItem value={QrCodeExportFormat.webp}>WEBP</MenuItem>
              <MenuItem value={QrCodeExportFormat.svg}>SVG</MenuItem>
              <MenuItem value={QrCodeExportFormat.utf8}>UTF8</MenuItem>
            </CustomSelect>
          </Grid2>
          <Grid2 xs={6}>
            <TextField
              label={t("qrCodeGenerator.widthHeight")}
              variant="outlined"
              color="info"
              value={state.qrCodeWidthPx}
              onChange={(e) => {
                dispatch(qrCodeWidthChanged(Number(e.target.value)));
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>{t("qrCodeGenerator.px")}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid2>
          <Grid2 xs={6}>
            <TextField
              fullWidth={true}
              type="color"
              label={t("qrCodeGenerator.darkColor")}
              value={state.darkColor}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                dispatch(darkColorChanged(e.target.value));
              }}
            />
          </Grid2>
          <Grid2 xs={6}>
            <TextField
              fullWidth={true}
              type="color"
              label={t("qrCodeGenerator.lightColor")}
              value={state.lightColor}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                dispatch(lightColorChanged(e.target.value));
              }}
            />
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box sx={{ mt: 1 }}>
                <CustomButton
                  icon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
                  disabled={
                    state.embeddedData === undefined || state.embeddedData == ""
                  }
                  label={t("qrCodeGenerator.download")}
                  width={180}
                  onClick={() => {
                    dispatch(generateQrCodeAsync());
                  }}
                />
              </Box>
            </Stack>
          </Grid2>
          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("qrCodeGenerator.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("qrCodeGenerator.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("qrCodeGenerator.content.customizeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("qrCodeGenerator.content.customizeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("qrCodeGenerator.content.secureHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("qrCodeGenerator.content.secureDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("qrCodeGenerator.content.businessHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("qrCodeGenerator.content.businessDescription")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("qrCodeGenerator.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="compression"
              link="/Compression"
              smallCard={true}
              image={CompressionImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="imageConverter"
              link="/ImageConverter"
              smallCard={true}
              image={ImageConverterImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
